import { CalendarMonthOutlined, East } from '@mui/icons-material';
import {
  Button,
  Chip,
  IconButton,
  Skeleton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { isAfter } from 'date-fns';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Label from 'src/components/Label';
import SignAllIcon from 'src/icons/untitled-ui/duocolor/sign-all-icon';
import ReopenPeriodButton from './ReopenPeriodButton';
import { Permissions, usePermission } from 'src/rbac';
import { enterpriseRbacEnforcementTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { CUSTOM, MONTHLY, isNeverClosed, signerLine } from './customCloseout';
import { closeoutTestId, closeoutRowTestId, periodKey } from './test-ids';

type Props = {
  item?: any;
  handleNavigateToMonthEndAlert: any;
  handleClickOpen: any;
  handleReopenClick?: (alertData: any) => void;
  division: string;
  //  BOPS-115 — custom-only row actions; absent on the Weekly / Monthly tabs.
  handleEditPeriod?: (alertData: any) => void;
  handleCancelPeriod?: (alertData: any) => void;
  //  Switches to the Custom range tab from a custom-managed month's Monthly row. The row is
  //  passed so the Custom picker can open on that month rather than on today.
  handleGoToCustomTab?: (alertData: any) => void;
  /**
   * BOPS-115 feature toggle. Off means the whole custom-closeout capability is hidden, which has
   * to include the Monthly tab's custom-managed treatment — otherwise a facility with custom
   * periods on the server would lose Close Month and be pointed at a tab that isn't rendered.
   */
  customCloseoutEnabled?: boolean;
  /**
   * BOPS-115 (8/8 mocks) — the month the Custom range picker has been moved to, e.g. "February
   * 2026", or null while it sits on its current-month default. The list endpoint only ever answers
   * for one month, so "none exist anywhere" can't be read off the response; the default month
   * standing empty is the closest honest signal, and any other month standing empty is a filter
   * result. The two get different copy because the fix differs — create one, versus move the picker.
   */
  customFilterLabel?: string | null;
};

const MonthEndAlertTable = (props: Props) => {
  const {
    item = [],
    handleNavigateToMonthEndAlert,
    handleClickOpen,
    handleReopenClick,
    handleEditPeriod,
    handleCancelPeriod,
    handleGoToCustomTab,
    customCloseoutEnabled = false,
    customFilterLabel = null,
  } = props;
  const canReopenClosedMonth = usePermission(Permissions.REOPEN_CLOSED_MONTH_ALERTS);
  const canCertifyAndSign = usePermission(Permissions.CERTIFY_AND_SIGN_MONTH_END_ALERTS);
  //  Same enterprise RBAC treatment the prelim review and billing-service key off: off (or not yet
  //  resolved) is fail-open, so prod keeps today's behaviour until the treatment is flipped.
  const [isRbacEnforced] = useFeatureToggleWhenReady(
    enterpriseRbacEnforcementTreatment,
    'ENTERPRISE'
  );

  const monthCloseOutResponse = useSelector((state: any) => state.monthlyCloseOutAlertReducer);
  const isMonthly = props?.division === MONTHLY;
  const isCustom = props?.division === CUSTOM;
  //  BOPS-293: a hard close needs the grant once enforcement is on; the weekly soft close is not gated.
  const canCloseHardPeriod =
    !(isMonthly || isCustom) || isRbacEnforced !== true || canCertifyAndSign;
  return (
    //  The date / alerts / action cells hold their content on one line, so a narrow viewport
    //  scrolls the table instead of wrapping every row to a different height.
    <Box sx={{ position: 'relative', overflowX: 'auto' }}>
      <Table
        sx={{ minWidth: 700 }}
        data-testid={closeoutTestId('alert-table')}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 48 }}>#</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {isCustom ? 'Date Range' : 'Cycle Date '}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>Number of Alerts</TableCell>
            <TableCell>{isMonthly || isCustom ? 'Closeout Status' : 'Status'}</TableCell>
            <TableCell
              align="right"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {monthCloseOutResponse?.loading ? (
            <>
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    textAlign="center"
                    p={4}
                  >
                    {}{' '}
                    {['1', '2', '3', '4', '5', '6']?.map((res) => {
                      return (
                        <Typography
                          key={res}
                          variant="h4"
                        >
                          <Skeleton animation="wave" />
                        </Typography>
                      );
                    })}
                  </Box>
                </TableCell>
              </TableRow>
            </>
          ) : item?.length === 0 ? (
            <TableRow data-testid={closeoutTestId('alert-table-empty')}>
              <TableCell colSpan={6}>
                <Box
                  textAlign="center"
                  p={3}
                >
                  {/*  The custom tab starts empty by design — a facility only creates a period for
                      an exception — so it explains itself rather than reading as a failed load. */}
                  {isCustom ? (
                    <>
                      <CalendarMonthOutlined sx={{ fontSize: 40, color: '#6366F1', mb: 1 }} />
                      <Typography variant="subtitle1">
                        {customFilterLabel
                          ? 'No custom closeouts for the selected month'
                          : 'No custom closeouts yet'}
                      </Typography>
                      <Typography
                        color={'text.secondary'}
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        {customFilterLabel
                          ? `None for ${customFilterLabel}. Change the month picker to see custom closeouts from other periods.`
                          : 'Create a closeout period above to close out a specific span of dates within a month.'}
                      </Typography>
                    </>
                  ) : (
                    <Typography
                      color={'text.secondary'}
                      variant="subtitle2"
                    >
                      No Records Found
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            item?.map((res: any, index: any) => {
              const isReopen = res?.status === 'REOPEN';
              //  BOPS-115 — a month holding custom periods is finished on the Custom range tab, so
              //  its monthly row loses the Close action the moment the first custom period exists.
              const isCustomManagedMonth =
                customCloseoutEnabled && isMonthly && res?.customManaged === true;
              //  Only sent alongside customManaged, and only on the monthly tab; anything the API
              //  does not recognise is left off the label rather than printed raw.
              //  Coloured by the periods' verdict, using the same greens and reds the rest of the
              //  table already gives CLOSED & SIGNED and OPEN, so the month reads consistently with
              //  every other row rather than staying custom-closeout purple whatever the state.
              const customManagedStatusLabel = !isCustomManagedMonth
                ? null
                : res?.customManagedStatus === 'CLOSED_AND_SIGNED'
                ? { text: 'CLOSED & SIGNED', backgroundColor: '#15B79E1F', color: '#107569' }
                : res?.customManagedStatus === 'OPEN'
                ? { text: 'OPEN', backgroundColor: '#F044381F', color: '#B42318' }
                : null;
              const canCloseAndSign =
                (res?.status === 'OPEN' || isReopen) && res?.errors === 0 && !isCustomManagedMonth;
              //  Edit / Cancel unwind a period that has never been locked; both vanish once it has
              //  been closed & signed, even if it was later reopened.
              const canEditOrCancel = isCustom && isNeverClosed(res);
              //  BOPS-118 — the row takes the API at its word: `signedByName` arrives only while
              //  that toggle is on, so its presence is the whole condition and no frontend flag is
              //  involved. (The Sign Certification modal can't do this — it opens before the close,
              //  when no signer exists yet — so that one reads Split directly.) Null on the Weekly
              //  tab whatever the API sends: the signer belongs to the hard closeouts only.
              const closedBy = signerLine(res, props?.division);
              let isAfterToday = false;

              try {
                isAfterToday = isAfter(new Date(res?.range.startDate), Date.now());
              } catch (e) {
                isAfterToday = false;
                // ignore exception
              }
              const rowKey = periodKey(res?.range);

              return (
                <TableRow
                  key={index}
                  data-testid={closeoutRowTestId('period-row', rowKey)}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${moment(res?.range?.startDate).format(
                    'MMM D, YYYY'
                  )} - ${moment(res?.range?.endDate).format('MMM D, YYYY')}`}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {res?.errors == 0 && res?.warnings == 0 && res?.ignores == 0 ? (
                      <>No alerts</>
                    ) : (
                      <>
                        {`${res?.errors} Errors | ${res?.warnings} Warnings | ${res?.ignores} Ignores`}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}
                    >
                      {res?.status === 'CLOSED_AND_SIGNED' ? (
                        <>
                          <Label
                            backgroundColor="#15B79E1F"
                            color="#107569"
                            text={res?.status?.split('_')?.join(' ')?.replace('AND', '&')}
                          />
                          {canReopenClosedMonth && (isMonthly || isCustom) && (
                            <ReopenPeriodButton
                              alertData={res}
                              onClick={handleReopenClick}
                              testId={closeoutRowTestId('period-row', rowKey, 'reopen')}
                            />
                          )}
                          {/*  Full width so it wraps onto its own line under the label and the
                              Reopen action, rather than competing with them for the row. */}
                          {closedBy && (
                            <Typography
                              variant="caption"
                              sx={{ width: '100%', color: '#6C737F' }}
                            >
                              {closedBy}
                            </Typography>
                          )}
                        </>
                      ) : isCustomManagedMonth ? (
                        <Box>
                          {/*  Checked ahead of REOPENED as well as READY: the month row carries a
                              status of its own — OPEN for most of a custom-managed month's life, or
                              REOPEN if anyone ever reopened it — but on this kind of month that
                              status describes a row nobody closes. The periods are what get closed,
                              so customManagedStatus is the only true reading and a stale REOPENED
                              must not hide it.
                              */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Label
                              backgroundColor="#EBEEFE"
                              color="#6366F1"
                              text="CUSTOM CLOSEOUT"
                            />
                            {customManagedStatusLabel && (
                              <>
                                <Typography sx={{ color: '#6C737F' }}>/</Typography>
                                <Label
                                  backgroundColor={customManagedStatusLabel.backgroundColor}
                                  color={customManagedStatusLabel.color}
                                  text={customManagedStatusLabel.text}
                                />
                              </>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: '#6C737F', mt: 0.5 }}
                          >
                            Managed with custom closeouts · close it from the Custom range tab
                          </Typography>
                        </Box>
                      ) : isReopen ? (
                        <Label
                          backgroundColor="#F79E1B1F"
                          color="#B54708"
                          text="REOPENED"
                        />
                      ) : canCloseAndSign ? (
                        <Label
                          backgroundColor="#EBEEFE"
                          color="#6366F1"
                          text={isAfterToday ? 'IN FUTURE' : 'READY'}
                        />
                      ) : (
                        <Label
                          backgroundColor="#F044381F"
                          color="#B42318"
                          text={res?.status}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {/*  Mock order: Edit dates · Cancel · Close · arrow — all on one line. Without
                        a flex row the cell is inline content, so the arrow drops onto its own line
                        as soon as the actions ahead of it fill the column. */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        flexWrap: 'nowrap',
                        gap: 1,
                      }}
                    >
                      {canEditOrCancel && (
                        <>
                          <Button
                            size="small"
                            onClick={() => handleEditPeriod?.(res)}
                            data-testid={closeoutRowTestId('period-row', rowKey, 'edit-dates')}
                            sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                          >
                            <Typography variant="buttonText">Edit dates</Typography>
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleCancelPeriod?.(res)}
                            data-testid={closeoutRowTestId('period-row', rowKey, 'cancel')}
                            sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                          >
                            <Typography variant="buttonText">Cancel</Typography>
                          </Button>
                        </>
                      )}
                      {/*  A month handed over to custom periods points at the tab that owns it
                        instead of offering a Close it can no longer perform. */}
                      {isCustomManagedMonth && (
                        <Button
                          size="small"
                          endIcon={<East fontSize="small" />}
                          onClick={() => handleGoToCustomTab?.(res)}
                          data-testid={closeoutRowTestId('period-row', rowKey, 'manage-on-custom')}
                          sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                        >
                          <Typography variant="buttonText">Manage on Custom range</Typography>
                        </Button>
                      )}
                      {canCloseAndSign && canCloseHardPeriod && !isAfterToday ? (
                        <Button
                          sx={{
                            height: '30px',
                            whiteSpace: 'nowrap',
                          }}
                          size="small"
                          startIcon={
                            <SvgIcon>
                              <SignAllIcon />
                            </SvgIcon>
                          }
                          variant="contained"
                          onClick={() => handleClickOpen(res)}
                          data-testid={closeoutRowTestId('period-row', rowKey, 'close')}
                        >
                          <Typography variant="buttonText">
                            {isCustom ? 'Close Period' : isMonthly ? 'Close Month' : 'Close Week'}
                          </Typography>
                        </Button>
                      ) : null}
                      {/*  BOPS-115 (8/8 mocks) — "Manage on Custom range" *replaces* the row's
                          open action on a custom-managed month rather than sitting beside it: both
                          arrows would lead somewhere, and only one of them is the way to close the
                          month. */}
                      {!isCustomManagedMonth && (
                        <IconButton
                          onClick={() => handleNavigateToMonthEndAlert(res?.range, res?.status)}
                          data-testid={closeoutRowTestId('period-row', rowKey, 'open')}
                        >
                          <East />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default MonthEndAlertTable;
